import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const DEFAULT_PERMS = {
  manage_users:              false,
  assign_tasks:              false,
  view_all_tasks:            false,
  update_task_status:        false,
  update_task_status_for_others: false,
  set_deadline_reminder:     false,
  assign_unassign_trademark: false,
  can_comment_task:          false,
};

const ALL_TRUE = Object.fromEntries(Object.keys(DEFAULT_PERMS).map(k => [k, true]));

const safeParseArr = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  const s = String(val).trim();
  if (s.startsWith('[')) { try { return JSON.parse(s); } catch (_) {} }
  return s.replace(/[\[\]"]/g, '').split(',').map(v => v.trim()).filter(Boolean);
};

export function useTeamPermission() {
  const { userType, license } = useAuth();
  const isClient = userType === 'client';

  const [perms, setPerms]                   = useState(isClient ? ALL_TRUE : DEFAULT_PERMS);
  const [hasTeamManagementAccess, setHasTMA] = useState(false);
  const [loading, setLoading]               = useState(true);
  // Bug fix: track whether the permissions API fetch failed (vs. user genuinely lacking perms)
  const [permsFetchFailed, setPermsFetchFailed] = useState(false);
  const [retryKey, setRetryKey]             = useState(0);

  // Expose a retry function so TeamManagementRoute can offer a "Retry" button
  const retryPerms = useCallback(() => setRetryKey(k => k + 1), []);

  useEffect(() => {
    if (!userType) return;

    let cancelled = false;
    setLoading(true);
    setPermsFetchFailed(false);

    (async () => {
      try {
        if (isClient) {
          // CLIENT: read directly from AuthContext license state (always fresh, not stale localStorage)
          const raw = license?.search_types_allowed || [];
          const allowed = Array.isArray(raw)
            ? raw
            : String(raw).replace(/[\[\]"]/g, '').split(',').map(v => v.trim()).filter(Boolean);
          const hasAccess = allowed.includes('Team_Management_Permission');
          if (!cancelled) {
            setHasTMA(hasAccess);
            setPerms(hasAccess ? ALL_TRUE : DEFAULT_PERMS);
          }
        } else {
          // CLIENT_USER: check parent license + role permissions
          // Bug fix: track individual API call outcomes so we can distinguish
          // "user lacks permission" from "API call failed"
          let permsOk = false;
          let detailsOk = false;

          const [permsRes, detailsRes] = await Promise.all([
            apiService.getMyTeamPermissions().catch(err => {
              // console.warn('[useTeamPermission] getMyTeamPermissions failed:', err?.message);
              return { success: false, _fetchFailed: true };
            }),
            apiService.getClientChildDetails().catch(err => {
              // console.warn('[useTeamPermission] getClientChildDetails failed:', err?.message);
              return { success: false, _fetchFailed: true };
            }),
          ]);

          if (cancelled) return;

          if (permsRes.success) {
            permsOk = true;
            setPerms({ ...DEFAULT_PERMS, ...permsRes.data });
          }

          if (detailsRes.success) {
            detailsOk = true;
            const licenseAllowed = safeParseArr(
              detailsRes.data?.activeLicense?.search_types_allowed
            );
            setHasTMA(licenseAllowed.includes('Team_Management_Permission'));
          }

          // If BOTH API calls failed (not just "no permission"), mark as fetch-failed
          // so TeamManagementRoute shows a retry UI instead of /403
          if (!permsOk && !detailsOk &&
              (permsRes._fetchFailed || detailsRes._fetchFailed)) {
            setPermsFetchFailed(true);
          }
        }
      } catch (_) {
        if (!cancelled) {
          setHasTMA(false);
          setPerms(DEFAULT_PERMS);
          setPermsFetchFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userType, license, retryKey]);

  return { perms, loading, isClient, hasTeamManagementAccess, permsFetchFailed, retryPerms };
}